import { InfiniteData } from '@tanstack/react-query'
import {
  type OpalApiResponseDocument,
  findResourceOfType,
  type OpalApiComponent,
} from '@opallabs/api'
import type { User, UserGroup } from '@opallabs/concepts'

type UsersResponseDocument = OpalApiResponseDocument<'ReadUsersV2', 200>
type UserGroupsResponseDocument = OpalApiResponseDocument<
  'ReadUserGroupsV3',
  200
>

export function usersSelector(
  document?: InfiniteData<UsersResponseDocument>
): Array<User> {
  if (!document) return []
  return document.pages.reduce<Array<User>>((allUsers, page) => {
    const users: Array<User> = page.data.map((user) => {
      const assetReference = page.included?.find(
        findResourceOfType(
          'asset_reference',
          'V2',
          user.relationships.avatar_asset_reference?.data?.id
        )
      )
      return {
        type: 'user',
        uuid: user.attributes.uuid,
        name: user.attributes.full_name,
        legacyId: user.id,
        asset: assetReference
          ? {
              id: assetReference.attributes.uuid,
              url: assetReference.attributes.full_url ?? '',
              title: assetReference.attributes.filename || '',
            }
          : undefined,
      }
    })
    return [...allUsers, ...users]
  }, [])
}

export function userGroupsSelector(
  responseDocument: UserGroupsResponseDocument
) {
  const visibleGroups = responseDocument.data.filter(
    (g) => !g.attributes.is_archived
  )

  return visibleGroups.map<UserGroup>((group) => {
    const groupAsset = responseDocument.included?.find(
      findResourceOfType('asset', 'V3', group.relationships.asset?.data?.id)
    )
    const groupMembers: Array<OpalApiComponent<'V3', 'user'>> = []
    group.relationships.members.data.forEach((user) => {
      const member = responseDocument.included?.find(
        findResourceOfType('user', 'V3', user.id)
      )
      if (member) groupMembers.push(member)
    })

    return {
      type: 'user_group',
      uuid: group.id,
      name: group.attributes.name,
      description: group.attributes.description ?? undefined,
      archived: Boolean(group.attributes.is_archived),
      asset: groupAsset
        ? {
            id: groupAsset.id,
            title: groupAsset.attributes.file_name,
            url: groupAsset.attributes.url,
          }
        : undefined,
      members: groupMembers.map((member) => ({
        type: 'user',
        uuid: member.id,
        name: member.attributes.full_name,
        legacyId: member.attributes.legacy_id,
      })),
      memberCount: group.relationships.members.data.length,
    }
  })
}
